import { useState, useRef, useCallback } from 'react';
import { languageAPI } from '../api/client';

interface UseVoiceRecorderResult {
  recording: boolean;
  transcribing: boolean;
  error: string | null;
  startRecording: () => Promise<void>;
  stopAndTranscribe: () => Promise<{ text: string; detected_language: string | null } | null>;
  clearError: () => void;
}

const SUPPORTED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

function getSupportedMimeType(): string | undefined {
  return SUPPORTED_MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t));
}

export function useVoiceRecorder(): UseVoiceRecorderResult {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string>('audio/webm');

  const clearError = useCallback(() => setError(null), []);

  const startRecording = useCallback(async () => {
    setError(null);

    const mimeType = getSupportedMimeType();
    if (!mimeType) {
      throw new Error('Your browser does not support audio recording');
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mimeTypeRef.current = mimeType;
    const mediaRecorder = new MediaRecorder(stream, { mimeType });
    chunksRef.current = [];

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setRecording(true);
  }, []);

  const stopAndTranscribe = useCallback(async () => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state !== 'recording') return null;

    return new Promise<{ text: string; detected_language: string | null } | null>((resolve) => {
      mediaRecorder.onstop = async () => {
        mediaRecorder.stream.getTracks().forEach(t => t.stop());
        setRecording(false);

        const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
        setTranscribing(true);
        try {
          const result = await languageAPI.transcribe(blob);
          resolve(result);
        } catch {
          resolve(null);
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorder.stop();
    });
  }, []);

  return { recording, transcribing, error, startRecording, stopAndTranscribe, clearError };
}
